import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Copy, Check, ExternalLink, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiEnrichScan } from "@/lib/api";

// Platform packs. Vinted, Depop and Poshmark are manual-copy (no legit listing API);
// eBay has a real listing API but requires user OAuth we haven't wired yet — for now
// the button copies the listing and opens eBay's Sell-Your-Item page in a new tab.
const PLATFORMS = [
  { id: "depop",    label: "Depop",    hint: "Aesthetic · lowercase · hashtags",  action: "copy" },
  { id: "ebay",     label: "eBay",     hint: "Technical · specs · measurements",  action: "open", url: "https://www.ebay.com/sl/sell" },
  { id: "poshmark", label: "Poshmark", hint: "Bundle-friendly · clean",           action: "copy" },
];

function useCopy() {
  const [copied, setCopied] = useState(null);
  const copy = async (id, text) => {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(text);
      else {
        const ta = document.createElement("textarea");
        ta.value = text; ta.style.position = "fixed"; ta.style.left = "-9999px";
        document.body.appendChild(ta); ta.select(); document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(id);
      toast.success(`Copied for ${id.charAt(0).toUpperCase() + id.slice(1)}`);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Copy failed — please select and copy manually");
    }
  };
  return { copied, copy };
}

/**
 * Build a Vinted-friendly listing string from the existing Depop copy.
 * We derive rather than re-prompt the LLM to save cost — Depop copy is the
 * best template for Vinted (lowercase, casual, hashtags).
 */
function buildVintedText(descriptions) {
  const base = descriptions?.depop || descriptions?.poshmark || "";
  // Add a short Vinted note if not already there
  const suffix = "\n\n📦 fast shipping · smoke-free home · check my other listings on Vinted!";
  return base.trim() ? base.trim() + suffix : "";
}

export default function CopySwitcher({ descriptions, scanId }) {
  const [tab, setTab] = useState("depop");
  const { copied, copy } = useCopy();
  // §2.5: descriptions are generated on-demand via Claude Haiku 4.5 when the
  // user opens the FIRST tab. Skip auto-fetch if the initial scan already has
  // content (backwards compat) or if we have no scan_id (demo path).
  const initialHas =
    descriptions && (descriptions.depop || descriptions.ebay || descriptions.poshmark);
  const [local, setLocal] = useState(initialHas ? descriptions : null);
  const [loading, setLoading] = useState(false);
  const [tapped, setTapped] = useState(false);
  const [enrichError, setEnrichError] = useState(null);

  useEffect(() => {
    // Fetch listings once the user first interacts with any tab.
    // enrichError guards against infinite retry: once a request fails, wait
    // for an explicit retry click rather than looping on every re-render.
    if (!tapped || local || loading || !scanId || enrichError) return;
    setLoading(true);
    apiEnrichScan(scanId, "listings")
      .then((r) => { setLocal(r.descriptions || {}); setEnrichError(null); })
      .catch((e) => {
        const msg = e?.jobError || e?.message || "generation_failed";
        setEnrichError(msg === "generation_timeout"
          ? "Description generation timed out. Please retry."
          : "Couldn't generate listing copy — please retry.");
        toast.error("Couldn't generate listing copy — retry available");
      })
      .finally(() => setLoading(false));
  }, [tapped, local, loading, scanId, enrichError]);

  const retryEnrich = () => {
    setEnrichError(null);
    setTapped(true);
  };

  const effective = local || {};
  const text = effective?.[tab] || "";

  const onSelectTab = (id) => {
    setTapped(true);
    setTab(id);
  };

  const copyPackage = () => {
    setTapped(true);
    if (!local && scanId && !enrichError) {
      apiEnrichScan(scanId, "listings")
        .then((r) => {
          const d = r.descriptions || {};
          setLocal(d);
          setEnrichError(null);
          const pkg =
            `DEPOP\n${d.depop || ""}\n\nEBAY\n${d.ebay || ""}\n\nPOSHMARK\n${d.poshmark || ""}\n\nVINTED\n${buildVintedText(d)}`;
          copy("package", pkg);
        })
        .catch(() => {
          setEnrichError("Couldn't generate listing copy — please retry.");
          toast.error("Couldn't generate listing copy — retry available");
        });
      return;
    }
    const d = effective;
    const pkg =
      `DEPOP\n${d.depop || ""}\n\nEBAY\n${d.ebay || ""}\n\nPOSHMARK\n${d.poshmark || ""}\n\nVINTED\n${buildVintedText(d)}`;
    copy("package", pkg);
  };

  const openEbay = () => {
    copy("ebay", effective?.ebay || "");
    window.open("https://www.ebay.com/sl/sell", "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bento-card p-5 sm:p-6" data-testid="copy-switcher">
      <div className="flex items-start justify-between mb-5 gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#2563EB]">
            Listing copy · 4 platforms
          </div>
          <h3 className="font-display text-lg sm:text-xl font-bold text-[#0F172A] mt-1">Quick Post</h3>
        </div>
        <button
          data-testid="copy-package-btn"
          onClick={copyPackage}
          className="px-4 py-2 rounded-full text-xs sm:text-sm font-semibold bg-[#0F172A] text-white hover:bg-[#1E293B] transition-all active:scale-[0.98] hover:scale-[1.02] flex items-center gap-2"
        >
          {copied === "package" ? <><Check size={13} strokeWidth={3} /> Copied</> : <><Copy size={13} /> Copy all 4</>}
        </button>
      </div>

      {/* Tabs — light pill switcher */}
      <div className="flex gap-1 p-1 rounded-full bg-[#F1F5F9] w-full sm:w-fit mb-4">
        {PLATFORMS.map((t) => (
          <button
            key={t.id}
            data-testid={`copy-tab-${t.id}`}
            onClick={() => onSelectTab(t.id)}
            className={`relative flex-1 sm:flex-initial px-4 py-1.5 text-xs sm:text-sm font-medium rounded-full transition-colors ${
              tab === t.id ? "text-white" : "text-[#64748B] hover:text-[#0F172A]"
            }`}
            {...(tab === t.id ? { "data-force-white": "true" } : {})}
          >
            {tab === t.id && (
              <motion.div
                layoutId="tab-pill"
                className="absolute inset-0 rounded-full bg-[#0F172A]"
                transition={{ type: "spring", damping: 24, stiffness: 300 }}
              />
            )}
            <span className="relative">{t.label}</span>
          </button>
        ))}
      </div>
      <div className="text-[11px] text-[#64748B] mb-3">{PLATFORMS.find((p) => p.id === tab)?.hint}</div>

      <AnimatePresence mode="wait">
        <motion.pre
          key={tab + (loading ? ":loading" : "")}
          data-testid={`copy-text-${tab}`}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
          className="p-4 rounded-2xl bg-[#F8F9FA] border border-[#E9ECEF] text-[12px] sm:text-sm text-[#334155] whitespace-pre-wrap font-mono-num leading-relaxed max-h-64 overflow-auto hide-scrollbar min-h-[120px]"
        >
          {loading ? (
            <span className="flex items-center gap-2 text-[#64748B] text-xs">
              <Loader2 size={14} className="animate-spin" /> Generating your listing…
            </span>
          ) : enrichError ? (
            <div className="flex flex-col gap-2" data-testid="copy-enrich-error">
              <span className="text-[#991B1B] text-xs">{enrichError}</span>
              <button
                data-testid="copy-enrich-retry"
                onClick={retryEnrich}
                className="self-start inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0F172A] text-white text-xs font-semibold hover:bg-[#1E293B] transition-colors"
              >
                <Loader2 size={11} /> Retry
              </button>
            </div>
          ) : !local && !tapped ? (
            <span className="text-[#64748B] text-xs">Tap a platform to generate listing copy (Claude Haiku — instant &amp; cheap).</span>
          ) : (
            text || <span className="text-[#64748B] text-xs">no copy available yet</span>
          )}
        </motion.pre>
      </AnimatePresence>

      {/* Per-platform 1-click buttons */}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
        {PLATFORMS.map((p) => (
          <button
            key={p.id}
            data-testid={`quick-post-${p.id}`}
            onClick={() => { setTapped(true); return p.action === "open" ? openEbay() : copy(p.id, effective?.[p.id] || ""); }}
            className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-xs font-semibold border transition-all active:scale-[0.98] hover:scale-[1.02] ${
              copied === p.id
                ? "bg-[#10B981] text-white border-[#10B981]"
                : "bg-white border-[#E9ECEF] text-[#0F172A] hover:border-[#0F172A]"
            }`}
          >
            {copied === p.id ? <><Check size={12} strokeWidth={3} /> Copied</>
              : p.action === "open" ? <><ExternalLink size={12} /> Post to {p.label}</>
              : <><Copy size={12} /> Copy for {p.label}</>}
          </button>
        ))}
        <button
          data-testid="quick-post-vinted"
          onClick={() => { setTapped(true); copy("vinted", buildVintedText(effective)); }}
          className={`flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-xs font-semibold border transition-all active:scale-[0.98] hover:scale-[1.02] col-span-2 sm:col-span-4 ${
            copied === "vinted"
              ? "bg-[#10B981] text-white border-[#10B981]"
              : "bg-white border-[#E9ECEF] text-[#0F172A] hover:border-[#0F172A]"
          }`}
        >
          {copied === "vinted" ? <><Check size={12} strokeWidth={3} /> Copied — paste in Vinted</>
            : <><Sparkles size={12} /> Copy for Vinted (auto-formatted)</>}
        </button>
      </div>
      <p className="mt-3 text-[10px] text-[#94A3B8] leading-relaxed">
        eBay opens the Sell page with your listing pre-copied — one paste and you're live. Vinted, Depop and Poshmark are copy-paste (their public listing APIs don't allow third-party posting).
      </p>
    </div>
  );
}
